import { Outlet, NavLink } from "react-router-dom";
import { 
  LayoutDashboard, 
  Receipt, 
  Calculator, 
  FileText, 
  Users, 
  ClipboardCheck,
  BookOpen,
  LogOut,
  User,
  Building2,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["company_admin", "user"] },
  { path: "/transactions", label: "Transactions", icon: Receipt, roles: ["company_admin", "user"] },
  { path: "/accounts", label: "Chart of Accounts", icon: BookOpen, roles: ["company_admin", "user"] },
  { path: "/calculator", label: "Carbon Calculator", icon: Calculator, roles: ["company_admin", "user"] },
  { path: "/reports", label: "Reports", icon: FileText, roles: ["company_admin", "user"] },
  { path: "/suppliers", label: "Suppliers", icon: Users, roles: ["company_admin", "user"] },
  { path: "/compliance", label: "CSRD Compliance", icon: ClipboardCheck, roles: ["company_admin", "user"] },
];

const adminNavItems = [
  { path: "/admin/users", label: "User Management", icon: Shield, roles: ["platform_owner", "company_admin"] },
  { path: "/admin/companies", label: "Company Management", icon: Building2, roles: ["platform_owner"] },
];

export const Layout = ({ user, onLogout }) => {
  const userRole = user?.role || "user";
  
  const filteredNavItems = navItems.filter(item => item.roles.includes(userRole));
  const filteredAdminItems = adminNavItems.filter(item => item.roles.includes(userRole));
  
  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="sidebar w-64 fixed h-full flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00c853] flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#1a2e22]" />
            </div>
            <div>
              <h1 className="text-white font-semibold text-lg tracking-tight font-['Outfit']">
                Ledger Green
              </h1>
              <p className="text-white/50 text-xs">Sustainability Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-link flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${
                  isActive ? "active" : ""
                }`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
          
          {/* Admin Section */}
          {filteredAdminItems.length > 0 && (
            <>
              <div className="pt-4 pb-2 px-4">
                <p className="text-white/40 text-xs uppercase tracking-wider font-semibold">Admin</p>
              </div>
              {filteredAdminItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-link flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${
                      isActive ? "active" : ""
                    }`
                  }
                  data-testid={`nav-${item.path.replace("/", "-")}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start px-4 py-3 h-auto text-white/70 hover:text-white hover:bg-white/10"
                data-testid="user-menu"
              >
                <div className="w-8 h-8 rounded-full bg-[#00c853] flex items-center justify-center mr-3">
                  <User className="w-4 h-4 text-[#1a2e22]" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{user?.name || "User"}</p>
                  <p className="text-xs text-white/50 truncate max-w-[120px]">
                    {user?.email || ""}
                  </p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-[#5c6b63]">
                <User className="w-4 h-4 mr-2" />
                {user?.company_name || user?.company || "No company"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onLogout}
                className="text-red-600 focus:text-red-600"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Progress - Hidden for Platform Owner */}
        {userRole !== "platform_owner" && (
          <div className="p-4 border-t border-white/10">
            <div className="px-4 py-3 rounded-xl bg-white/5">
              <p className="text-white/50 text-xs mb-1">Carbon Offset Target</p>
              <div className="progress-bar bg-white/10">
                <div className="progress-bar-fill" style={{ width: "65%" }} />
              </div>
              <p className="text-white/70 text-xs mt-1">65% achieved</p>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="ml-64 flex-1 p-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
